import React from 'react'
import ReactDOM from 'react-dom/client'
import posthog from 'posthog-js'
import {
 PostHogProvider
} from 'posthog-js/react'
import {
 BrowserRouter, useLocation
} from 'react-router-dom'
import {
 QueryClient, QueryClientProvider
} from '@tanstack/react-query'
import App from './App'
import {
 ErrorBoundary
} from './components/ErrorBoundary'
import './index.css'
import {
 initPostHog,
 markIPhoneUse,
 trackReferralVisitFromUrl
} from './analytics/posthog'
import {
 watchForUnfinishedSignIn
} from './analytics/signInReturn'
import {
  isIsolatedRendererPath
} from './utils/routes'

const isRenderer = isIsolatedRendererPath(window.location.pathname)

// A private renderer receives agent-owned brief data after the bundle loads.
// Keep analytics entirely uninitialized on this route so that data never leaves
// the isolated browser session.
if (!isRenderer) {
  initPostHog()
  // "Website shown": if the visitor arrived via a referral link (?ref=), record
  // the funnel step + pin attribution. Runs after init so PostHog is ready, and
  // before the router can navigate the ?ref away.
  trackReferralVisitFromUrl()
  // Sticky device fact for iPhone-only flags; see the function.
  markIPhoneUse()
  // Before the router runs, so `/login?error=` is still on the URL to read.
  watchForUnfinishedSignIn()
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

function RootErrorBoundary({ children }: { children: React.ReactNode }): JSX.Element {
  const { pathname } = useLocation()
  return <ErrorBoundary boundary="root" resetKey={pathname}>{children}</ErrorBoundary>
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found')
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <RootErrorBoundary>
            <App />
          </RootErrorBoundary>
        </QueryClientProvider>
      </BrowserRouter>
    </PostHogProvider>
  </React.StrictMode>,
)
